import { z } from "zod";

export const PersonaSchema = z.object({
  name: z.string().trim().min(1),
  role: z.string().trim().optional(),
  level: z.enum(["beginner", "intermediate", "expert"]).default("intermediate"),
  pains: z.array(z.string()).default([]),
  goals: z.array(z.string()).default([]),
});

export const WriterSchema = z.object({
  name: z.string().trim().min(1),
  expertise: z.string().trim().optional(),
});

export const ProjectSchema = z.object({
  name: z.string().trim().min(1),
  domain: z.string().trim().optional(),
  lang: z.string().trim().optional(),
  gsc_property: z.string().trim().optional(),
  sitemap_url: z.string().url().optional(),
});

export const MetaSchema = z.object({
  title: z.string().max(60),
  description: z.string().max(160),
  slug: z.string().min(1),
});

export const JsonLdSchema = z.object({
  article: z.any(),
  faq: z.any().optional(),
});

export const ScoreBreakdownSchema = z.object({
  intent: z.number().min(0).max(100),
  readability: z.number().min(0).max(100),
  headings: z.number().min(0).max(100),
  keywords: z.number().min(0).max(100),
  eeat: z.number().min(0).max(100),
  interlinking: z.number().min(0).max(100),
  meta: z.number().min(0).max(100),
  faq: z.number().min(0).max(100),
  jsonld: z.number().min(0).max(100),
});

export const ScoreSchema = z.object({
  total: z.number().min(0).max(100),
  breakdown: ScoreBreakdownSchema,
  actions: z.array(z.string()).default([]),
});

export const ContentInputSchema = z.object({
  primaryKeyword: z.string().min(1),
  secondaryKeywords: z.array(z.string()).default([]),
  language: z.string().min(2),
  searchIntent: z
    .enum(["informational", "commercial", "transactional", "navigational"])
    .default("informational"),
  persona: PersonaSchema.optional(),
  tone: z.string().default("neutral"),
  length: z.enum(["short", "medium", "long"]).default("medium"),
  structure: z
    .object({
      enforceH1: z.boolean().default(true),
      maxDepth: z.number().int().min(2).max(4).default(3),
    })
    .default({ enforceH1: true, maxDepth: 3 }),
  brandConstraints: z.string().default(""),
  internalLinksCandidates: z.array(z.object({ anchor: z.string(), slug: z.string() })).default([]),
  targetCountry: z.string().length(2),
  eeatProofs: z.array(z.string()).default([]),
  contentType: z.enum(["article", "landing"]).default("article"),
  withFaq: z.boolean().default(false),
  useFormatting: z.boolean().default(false),
  imageCount: z.number().int().min(0).max(8).default(0),
  autoMeta: z.boolean().default(false),
  // Advanced SEO options
  targetEntities: z.array(z.string()).default([]),
  competitorUrls: z.array(z.string().url().or(z.string())).default([]),
  serpFeatures: z
    .array(z.enum(["featured_snippet", "paa", "images", "videos", "news"]).or(z.string()))
    .default([]),
  cta: z.string().default(""),
  readingLevel: z.enum(["simple", "pro", "expert"]).default("pro"),
  requiredSections: z.array(z.string()).default([]),
  forbidden: z.array(z.string()).default([]),
  wordCount: z
    .object({
      min: z.number().int().positive().optional(),
      max: z.number().int().positive().optional(),
    })
    .default({}),
});

export const GenerationStep = z.enum([
  "brief",
  "outline",
  "draft",
  "faq",
  "meta",
  "jsonld",
  "links",
  "score",
]);

export const GenerationInputSchema = z.object({
  contentId: z.string().uuid(),
  step: GenerationStep,
  input: ContentInputSchema,
  options: z
    .object({
      temperature: z.number().min(0).max(2).default(0.3),
      maxTokens: z.number().int().min(256).max(8192).default(2048),
    })
    .optional(),
});

export const GenerationResultSchema = z.object({
  step: GenerationStep,
  data: z.any(),
  tokensIn: z.number().int().nonnegative(),
  tokensOut: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});

export type Persona = z.infer<typeof PersonaSchema>;
export type Writer = z.infer<typeof WriterSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type ContentInput = z.infer<typeof ContentInputSchema>;
export type GenerationInput = z.infer<typeof GenerationInputSchema>;
export type GenerationResult = z.infer<typeof GenerationResultSchema>;
export type Score = z.infer<typeof ScoreSchema>;
export type Meta = z.infer<typeof MetaSchema>;
export type JsonLD = z.infer<typeof JsonLdSchema>;
